import React, { useState, useCallback } from 'react'
import classNames from 'classnames/bind'
import AdminSidebar from '@/admin/shell/components/AdminSidebar/AdminSidebar'
import AdminHeader from '@/admin/shell/components/AdminHeader/AdminHeader'
import AdminContent from '@/admin/shell/components/AdminContent/AdminContent'
import AdminFooter from '@/admin/shell/components/AdminFooter/AdminFooter'
import layoutStyles from './AdminLayout.module.scss'
// Global admin design-system styles (tokens + base)
import '../../styles/admin.scss'

const cx = classNames.bind(layoutStyles)

type AdminTheme = 'light' | 'dark'

function getInitialTheme(): AdminTheme {
  if (typeof window === 'undefined') return 'light'
  const saved = localStorage.getItem('admin-theme') as AdminTheme | null
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

interface AdminLayoutProps {
  children: React.ReactNode
  title?: string
}

function AdminLayout({ children, title = 'Admin' }: AdminLayoutProps) {
  const [theme, setTheme] = useState<AdminTheme>(getInitialTheme)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light'
      localStorage.setItem('admin-theme', next)
      return next
    })
  }, [])

  return (
    <div
      data-admin-root
      data-theme={theme}
      className={cx('shell')}
    >
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          className="adm-backdrop adm-backdrop-open"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <AdminSidebar
        currentPath={currentPath}
        drawerOpen={drawerOpen}
        onDrawerClose={() => setDrawerOpen(false)}
      />

      <div className={cx('main')}>
        <AdminHeader
          title={title}
          theme={theme}
          onThemeToggle={toggleTheme}
          onMenuOpen={() => setDrawerOpen(true)}
        />
        <AdminContent>{children}</AdminContent>
        <AdminFooter />
      </div>
    </div>
  )
}

export default AdminLayout
